// Emergency mode (PMU-273): the app's side of emergency records on the Bluetooth mesh. A record
// (declare, end, roll call, class report, or a reunification release) is a signed event like any
// other: it goes into this device's outbox (uploaded when online) and, sealed with the school's
// key of the day, onto the mesh. Records heard on the mesh are opened, checked against the device
// that signed them, kept, shown, and uploaded for that device as soon as this one reaches the cloud.
import { b64url, fromB64url, uuidv7, verifyCanonical, type PreImage } from "@vavr/core";
import type { DeviceApi } from "./api.ts";
import type { VavrDb } from "./db.ts";
import { enqueue } from "./outbox.ts";
import { MESH, MESH_FLAG, buildMeshPacket, decodeMeshPacket } from "./mesh.ts";
import { readPeers, type Peer } from "./lan.ts";

export type EmergencyKind = "lockdown" | "evacuation" | "shelter" | "reunification";
export type RollCall = "present" | "missing" | "injured" | "with_adult" | "absent_today";
export interface MeshKey { date: string; key: string }
export interface MeshEvent { pre: PreImage; device_sig: string }

// ---------- the school's mesh keys ----------

/** Fetches yesterday's, today's and tomorrow's keys and keeps them sealed with the device's own key. */
export async function refreshMeshKeys(db: VavrDb, api: DeviceApi, mirrorKey: CryptoKey): Promise<MeshKey[]> {
  const { keys } = await api.json<{ keys: MeshKey[] }>("GET", "/api/v1/device/mesh-keys");
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, mirrorKey, new TextEncoder().encode(JSON.stringify(keys)));
  await db.meta.put({ key: "mesh-keys", value: { iv, ct } });
  return keys;
}
export async function readMeshKeys(db: VavrDb, mirrorKey: CryptoKey): Promise<MeshKey[]> {
  const v = (await db.meta.get("mesh-keys"))?.value as { iv: Uint8Array; ct: ArrayBuffer } | undefined;
  if (!v) return [];
  try { return JSON.parse(new TextDecoder().decode(await crypto.subtle.decrypt({ name: "AES-GCM", iv: v.iv as BufferSource }, mirrorKey, v.ct))) as MeshKey[]; } catch { return []; }
}

const today = (now = Date.now()) => { const d = new Date(now); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

/** Sealed with the key of the day: [10-byte date][12-byte iv][AES-GCM of the event as JSON]. */
export async function sealMeshEvent(keys: MeshKey[], ev: MeshEvent, now = Date.now()): Promise<Uint8Array> {
  const k = keys.find((x) => x.date === today(now)) ?? keys[1] ?? keys[0];
  if (!k) throw new Error("no mesh key on this device");
  const key = await crypto.subtle.importKey("raw", fromB64url(k.key) as BufferSource, "AES-GCM", false, ["encrypt"]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: new TextEncoder().encode(k.date) }, key, new TextEncoder().encode(JSON.stringify(ev))));
  const out = new Uint8Array(10 + 12 + ct.length);
  out.set(new TextEncoder().encode(k.date), 0); out.set(iv, 10); out.set(ct, 22);
  return out;
}
export async function openMeshEvent(keys: MeshKey[], payload: Uint8Array): Promise<MeshEvent | null> {
  if (payload.length < 22 + 16) return null;
  const date = new TextDecoder().decode(payload.slice(0, 10));
  const k = keys.find((x) => x.date === date);
  if (!k) return null;
  try {
    const key = await crypto.subtle.importKey("raw", fromB64url(k.key) as BufferSource, "AES-GCM", false, ["decrypt"]);
    const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: payload.slice(10, 22) as BufferSource, additionalData: new TextEncoder().encode(date) }, key, payload.slice(22) as BufferSource);
    return JSON.parse(new TextDecoder().decode(plain)) as MeshEvent;
  } catch { return null; }
}

// ---------- records ----------

export interface EmergencyContext { siteId: string; deviceId: string; staffId: string; monoStart: number }

/** An emergency event's pre-image (module schema v2). */
export function emergencyPre(ctx: EmergencyContext, type: `emergency:${string}`, childId: string | null, data: Record<string, string | number | boolean | null>, now = Date.now()): PreImage {
  return {
    v: 2, id: uuidv7(), site_id: ctx.siteId, type, group_id: null, child_id: childId, adult_id: null, claimed_name: null, id_check: null,
    staff_id: ctx.staffId, witness_staff_id: null, device_id: ctx.deviceId, device_ts: now, device_mono: Math.max(0, Math.round(now - ctx.monoStart)),
    photo_sha256: null, face: null, stroke_sha256: null, attestation: "Recorded during an emergency.", auth_method: "none", flags: [], reason: null, data,
  } as unknown as PreImage;
}

/** The native mesh, as far as this layer needs it. */
export interface MeshSender { send(packet: string): Promise<void> }

/**
 * One device's emergency records on the mesh. `onRecord` gets every record, this device's own and
 * those heard on the mesh (checked), once each, to update the screens.
 */
export class EmergencyMesh {
  private o: { db: VavrDb; deviceId: string; privateKey: CryptoKey; mirrorKey: CryptoKey; mesh: MeshSender; onRecord: (ev: MeshEvent, own: boolean) => void };
  private seen = new Set<string>();
  constructor(o: EmergencyMesh["o"]) { this.o = o; }

  /** Records an event here (outbox) and sends it over the mesh. Declarations and ends wake people. */
  async record(pre: PreImage, opts: { drill?: boolean } = {}): Promise<MeshEvent> {
    const entry = await enqueue(this.o.db, this.o.privateKey, pre, []);
    const ev: MeshEvent = { pre: entry.pre, device_sig: entry.device_sig };
    this.seen.add(pre.id);
    this.o.onRecord(ev, true);
    const keys = await readMeshKeys(this.o.db, this.o.mirrorKey);
    // Declarations, ends and alerts are urgent (a phone in a pocket raises a notification); a
    // message is its own kind so relays can tell it from a roll call.
    const urgent = pre.type === "emergency:declare" || pre.type === "emergency:end" || pre.type === "emergency:alert";
    const type = pre.type === "emergency:alert" ? MESH.ALERT : pre.type === "emergency:message" ? MESH.MESSAGE : urgent ? MESH.EMERGENCY : MESH.RECORD;
    const flags = (urgent ? MESH_FLAG.URGENT : 0) | (opts.drill ? MESH_FLAG.DRILL : 0);
    const packet = await buildMeshPacket(this.o.privateKey, { type, flags, origin: this.o.deviceId, to: "", created: Date.now(), payload: await sealMeshEvent(keys, ev) });
    await this.o.mesh.send(b64url(packet)).catch(() => undefined); // no mesh (online): the outbox carries it
    return ev;
  }

  /** An event already recorded on this device (a reunification release) shared over the mesh. */
  async share(ev: MeshEvent): Promise<void> {
    if (this.seen.has(ev.pre.id)) return;
    this.seen.add(ev.pre.id);
    const keys = await readMeshKeys(this.o.db, this.o.mirrorKey);
    if (!keys.length) return;
    const packet = await buildMeshPacket(this.o.privateKey, { type: MESH.RECORD, flags: 0, origin: this.o.deviceId, to: "", created: Date.now(), payload: await sealMeshEvent(keys, ev) });
    await this.o.mesh.send(b64url(packet)).catch(() => undefined);
  }

  /** A packet from the mesh: opened, checked against the device that signed the event, kept, shown. */
  async received(packetB64: string): Promise<MeshEvent | null> {
    const p = decodeMeshPacket(fromB64url(packetB64));
    if (!p || (p.type !== MESH.EMERGENCY && p.type !== MESH.RECORD && p.type !== MESH.ALERT && p.type !== MESH.MESSAGE)) return null;
    const ev = await openMeshEvent(await readMeshKeys(this.o.db, this.o.mirrorKey), p.payload);
    if (!ev?.pre?.id || this.seen.has(ev.pre.id)) return null;
    const peers: Peer[] = await readPeers(this.o.db);
    const signer = peers.find((x) => x.id === ev.pre.device_id);
    if (!signer || ev.pre.site_id !== (await this.o.db.device.get("device"))?.site_id || !(await verifyCanonical(signer.pubkey, ev.pre, ev.device_sig).catch(() => false))) return null;
    this.seen.add(ev.pre.id);
    const own = ev.pre.device_id === this.o.deviceId;
    if (!own) await this.o.db.meshrec.put({ id: ev.pre.id, pre: ev.pre, device_sig: ev.device_sig, at: Date.now(), uploaded: 0 });
    this.o.onRecord(ev, own);
    return ev;
  }

  /** Every record heard on the mesh so far (to rebuild the screens at start). */
  async heard(): Promise<MeshEvent[]> {
    return (await this.o.db.meshrec.orderBy("at").toArray()).map((r) => ({ pre: r.pre, device_sig: r.device_sig }));
  }

  /** Uploads what this device carried for others; each one the cloud answered for is marked done. */
  async uploadCarried(api: DeviceApi): Promise<number> {
    const pending = await this.o.db.meshrec.where("uploaded").equals(0).limit(100).toArray();
    if (!pending.length) return 0;
    const r = await api.json<{ results: { id: string | null; ok: boolean; error?: string }[] }>("POST", "/api/v1/device/events/carried", { json: { events: pending.map((x) => ({ pre: x.pre, device_sig: x.device_sig })) } });
    let done = 0;
    for (const x of r.results) {
      // Accepted, or refused for good (it would be refused again): either way the cloud has answered.
      if (x.id && (x.ok || (x.error && !/^(5\d\d|in_progress)/.test(x.error)))) { await this.o.db.meshrec.update(x.id, { uploaded: 1 }); done++; }
    }
    return done;
  }
}
